import React, { ReactNode, useState } from "react";
import { intl } from "LocaleWrapper";
import { useMutation, useQuery } from "@apollo/client";
import uniqBy from "lodash.uniqby";

import { Button, Card, PageHeader, TagSize } from "@cofano/cck";
import { Box, IconButton, Skeleton, Tooltip, useTheme } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import RefreshIcon from "@mui/icons-material/Refresh";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { useConfirm } from "components/hooks/useConfirm";

import Helmet from "components/helmet/Helmet";

import { DataFormat, Language } from "imports/Utils";

import { locationDescriptionWithGroup } from "view/locations/utils/LocationUtils";
import CargoActionTypeTag from "view/cargos/components/tags/CargoActionTypeTag";
import { updateCargoActionLocation_mutation } from "view/cargos/utils/CargoQueries";
import { CargoActionDate } from "view/cargos/components/date/CargoActionDate";
import CargoActionQuantity from "view/cargos/components/quantities/CargoActionQuantity";

import CreateConnectingShipBerthingDialog from "./components/dialogs/CreateConnectingShipBerthingDialog";
import UpdateBerthingLocationDialog from "./components/dialogs/UpdateBerthingLocationDialog";
import UpdateBerthingWindowDialog from "./components/dialogs/UpdateBerthingWindowDialog";
import UpdateVisitWindowDialog from "./components/dialogs/UpdateVisitWindowDialog";
import UpdateCallPtaDialog from "../calls/components/dialogs/UpdateCallPtaDialog";
import UpdateCallLocationDialog from "../calls/components/dialogs/UpdateCallLocationDialog";
import UpdateConnectingVisitNotesDialog from "./components/dialogs/UpdateConnectingVisitNotesDialog";

import { FormattedDate, FormattedDateWindow } from "utils/DateUtils";
import { DossierLink, ProductLink, RelationLink } from "utils/LinkUtils";

import { DeleteBerthingDocument, EditConnectingVisitUpdateTimeDocument, LinkActionToBerthingDocument } from "__generated__/view/connectingVisits/graphql/connectingVisit.mutations.generated";
import { GetConnectingVisitBerthPlanningDocument, GetConnectingVisitBerthPlanningQuery } from "__generated__/view/connectingVisits/graphql/connectingVisit.queries.generated";

import "./styling/styling.scss";
import { LocationSelectOption } from "view/locations/components/selects/LocationSelect";

const format = "DD-MM HH:mm";

const sortFunction = (a: string | number, b: string | number) => {
	if (a < b) {
		return -1;
	}
	if (a > b) {
		return 1;
	}
	return 0;
};

type BerthPlanning = NonNullable<GetConnectingVisitBerthPlanningQuery["connectingVisitBerthPlanning"]>;
type PlanningPerAgencies = NonNullable<BerthPlanning["planningPerAgency"]>;
type PlanningPerAgency = NonNullable<PlanningPerAgencies[0]>;
type Agency = NonNullable<PlanningPerAgency["agency"]>;
type PlanningPerLocations = NonNullable<PlanningPerAgency["planningPerLocation"]>;
type PlanningPerLocation = NonNullable<PlanningPerLocations[0]>;
type Location = NonNullable<PlanningPerLocation["location"]>;
type SubLocations = NonNullable<Location["subLocations"]>;
type Visits = NonNullable<PlanningPerLocation["visits"]>;
type Visit = NonNullable<Visits[0]>;
type ConnectingShipBerthings = NonNullable<Visit["connectingShipBerthings"]>;
type ConnectingShipBerthing = NonNullable<ConnectingShipBerthings[0]>;
type CargoActions = NonNullable<ConnectingShipBerthing["cargoActions"]>;
type CargoAction = NonNullable<CargoActions[0]>;
type Call = NonNullable<CargoAction["call"]>;
type ConnectingShipVisit = NonNullable<CargoAction["connectingShipPortVisit"]>;
type Ship = NonNullable<ConnectingShipVisit["ship"]>;

type Dragging = {
	enabled: boolean,
	managedShipLocation: Location | null,
	connectingShip: Ship | null,
	connectingShipBerthing: ConnectingShipBerthing | null,
};

type SetDragging = (options: Dragging) => void;

const ConnectingVisitPlanning = () => {

	const theme = useTheme();

	const { data, refetch, loading } = useQuery(GetConnectingVisitBerthPlanningDocument);

	const sortPlanning = (list: PlanningPerAgencies) => {
		return [ ...list ].sort((a, b) => sortFunction(a?.agency?.name || "", b?.agency?.name || ""));
	};

	const sortVisitByWindow = (
		a?: { from?: number | null; until?: number | null } | null,
		b?: { from?: number | null; until?: number | null } | null,
	) => {
		const aIncomplete = a?.from == null || a?.until == null || a?.from == 0 || a?.until == 0;
		const bIncomplete = b?.from == null || b?.until == null || b?.from == 0 || b?.until == 0;

		if (aIncomplete && !bIncomplete) {
			return 1;
		}
		if (!aIncomplete && bIncomplete) {
			return -1;
		}

		if (aIncomplete && bIncomplete) {
			return 0;
		}

		return (a!.from! - b!.from!);
	};

	const planning = sortPlanning(data?.connectingVisitBerthPlanning?.planningPerAgency || []);

	const [ selectedAction, setSelectedAction ] = useState<CargoAction | undefined>(undefined);

	return (
		<div className="connecting-visit-planning">
			<PageHeader>
				<PageHeader.Title>
					{Language("CONNECTING_VISIT_PLANNING")}
					<Helmet title={intl.formatMessage({ id: "CONNECTING_VISIT_PLANNING" })} />
				</PageHeader.Title>
			</PageHeader>

			<Card disablePadding>
				<Box sx={{ width: "100%" }}>
					<Box sx={{ maxHeight: "calc(100vh - 128px)", overflowX: "auto", width: "100%" }}>
						<table style={{ whiteSpace: "nowrap", width: "100%" }}>
							<thead
								style={{
									backgroundColor: theme.palette.mode === "dark" ? theme.palette.background.paper : "#f3f4f5",
									boxShadow: `inset 0 -1px 0 0 ${theme.palette.divider}`,
								}}>
								<tr>
									<th colSpan={6}>{Language("CONNECTING_SHIP")}</th>
									<th colSpan={13}>{Language("MANAGED_SHIP")}</th>
								</tr>
							</thead>

							<thead style={{ backgroundColor: theme.palette.background.paper }}>
								<tr>
									<th>{intl.formatMessage({ id: "AGENCY" })}</th>
									<th>{intl.formatMessage({ id: "PORT" })}</th>
									<th>{intl.formatMessage({ id: "SHIP" })}</th>
									<th>{intl.formatMessage({ id: "CONNECTING_VISIT_PORT_STAY" })}</th>
									<th>{intl.formatMessage({ id: "BERTH_LOCATION" })}</th>
									<th>{intl.formatMessage({ id: "CONNECTING_VISIT_BERTH_STAY" })}</th>
									<th>{intl.formatMessage({ id: "SHIP" })}</th>

									<th>{intl.formatMessage({ id: "CONNECTING_VISIT_EXPECTED_ARRIVAL" })}</th>
									<th>{intl.formatMessage({ id: "CONNECTING_VISIT_ACTION" })}</th>
									<th>{intl.formatMessage({ id: "PRODUCT" })}</th>
									<th>{intl.formatMessage({ id: "ORDER_QUANTITY" })}</th>
									<th>{intl.formatMessage({ id: "ORDER_LOADING_DATE" })}</th>
									<th>{intl.formatMessage({ id: "ORDER_LOADING_PLACE" })}</th>
									<th>{intl.formatMessage({ id: "ORDER_DISCHARGING_DATE" })}</th>
									<th>{intl.formatMessage({ id: "DISCHARGE_LOCATION" })}</th>
									<th>{intl.formatMessage({ id: "CUSTOMER" })}</th>
									<th>{intl.formatMessage({ id: "CHARTER" })}</th>
									<th>{intl.formatMessage({ id: "NOTE" })}</th>
								</tr>
							</thead>

							{loading ? (
								<LoadingBody />
							) : (planning.length > 0) ? (
								<tbody>
									{planning.map((it, index) => {
										const sortedLocations = [ ...(it?.planningPerLocation || []) ]
											.sort((a, b) => sortFunction(a?.location?.description || "", b?.location?.description || ""));

										return (
											<React.Fragment key={it?.agency?.id ?? index}>
												<AgentRow agency={it?.agency} />

												{sortedLocations.map((item, portIndex) => {
													if (!item?.location) {
														return null;
													}

													const sortedVisits = [ ...(item.visits || []) ].sort(sortVisitByWindow);

													return (
														<React.Fragment key={item.location.id ?? portIndex}>
															<PortRow location={item.location} />

															{sortedVisits.map((visit) => (
																<VisitRow
																	key={visit?.id}
																	visit={visit}
																	subLocations={[ item.location!, ...(item.location?.subLocations ?? []) ]}
																	refetch={refetch}
																	setSelectedAction={setSelectedAction}
																/>
															))}
														</React.Fragment>
													);
												})}
											</React.Fragment>
										);
									})}
								</tbody>
							) : (
								<tbody>
									<tr>
										<td colSpan={100} style={{ textAlign: "center" }}>Geen resultaten gevonden</td>
									</tr>
								</tbody>
							)}
						</table>
					</Box>
				</Box>
			</Card>

			{selectedAction && (
				<UpdateConnectingVisitNotesDialog
					open={!!selectedAction}
					onClose={() => setSelectedAction(undefined)}
					actionId={selectedAction.id}
					connectingShipPortVisitId={selectedAction.connectingShipPortVisit?.id ?? null}
					initialNotes={selectedAction.connectingVisitNotes ?? ""}
					refetch={refetch}
				/>
			)}
		</div>
	);
};

const LoadingBody = () => {
	return (
		<tbody>
			{[ ...Array(10) ].map((e, index) => (
				<tr key={index}>
					<td colSpan={100}>
						<Skeleton variant="text" sx={{ fontSize: "1rem" }} />
					</td>
				</tr>
			))}
		</tbody>
	);
};

const ClashField = ({ hasClash, disablePadding, children }: {
	hasClash?: boolean;
	disablePadding?: boolean;
	children?: ReactNode;
}) => {
	if (hasClash) {
		return (
			<span className={`clash ${disablePadding ? "disable-padding" : ""}`}>
				{children || <React.Fragment />}
			</span>
		);
	} else {
		return children || <React.Fragment />;
	}
};

const AgentRow = ({ agency }: { agency?: Agency | null }) => {
	const theme = useTheme();
	return (
		<tr style={{ backgroundColor: theme.palette.mode === "dark" ? "rgb(101, 185, 247)" : "rgb(214, 228, 241)"  }}>
			<td colSpan={6} className="dividerRight">
				<strong>{agency?.name || intl.formatMessage({ id: "CONNECTING_VISIT_NO_AGENCY" })}</strong>
			</td>

			<td colSpan={13} />
		</tr>
	);
};

const PortRow = ({ location }: { location?: Location | null }) => {
	const theme = useTheme();
	return (
		<tr style={{ backgroundColor: theme.palette.mode === "dark" ? theme.palette.background.paper : "#EEE"  }}>
			<td />

			<td colSpan={5} className="dividerRight">
				{locationDescriptionWithGroup(location)}
			</td>

			<td colSpan={13} />
		</tr>
	);
};

const VisitRow = ({ visit, subLocations, refetch, setSelectedAction }: {
	visit?: Visit | null,
	subLocations?: SubLocations,
	refetch: () => void,
	setSelectedAction: (action: CargoAction) => void
}) => {

	const [ dragging, setDragging ] = useState<Dragging>({
		enabled: false,
		managedShipLocation: null,
		connectingShip: null,
		connectingShipBerthing: null,
	});

	const cargoActions = visit?.cargoActions || [];
	const cargoActionsNotPlanned = [ ...cargoActions ].filter(a => !a?.connectingShipBerthing);
	const berthLocations = uniqBy([ ...(visit?.connectingShipBerthings?.map(it => it?.location) || []) ], "id") as Location[];

	return (
		<React.Fragment>
			<DroppableVisitRow
				visit={visit}
				dragging={dragging}
				refetch={refetch} />

			{[ ...(visit?.connectingShipBerthings || []) ]
				.sort((a, b) => {
					const aFrom = a?.window?.from ?? Number.MAX_SAFE_INTEGER;
					const bFrom = b?.window?.from ?? Number.MAX_SAFE_INTEGER;

					if (aFrom !== bFrom) {
						return aFrom - bFrom;
					}

					const aTo = a?.window?.to ?? Number.MAX_SAFE_INTEGER;
					const bTo = b?.window?.to ?? Number.MAX_SAFE_INTEGER;

					if (aTo !== bTo) {
						return aTo - bTo;
					}

					return (a?.id ?? 0) - (b?.id ?? 0);
				})
				.map((berthing) => (
					<BerthingRow
						berthing={berthing as ConnectingShipBerthing}
						visit={visit as Visit}
						dragging={dragging}
						setDragging={setDragging}
						berthLocations={berthLocations}
						subLocations={subLocations}
						refetch={refetch}
						setSelectedAction={setSelectedAction}
						key={berthing?.id}
					/>
				))}

			{cargoActionsNotPlanned.map((cargoAction) => (
				<CargoActionNotPlannedRow
					action={cargoAction as CargoAction}
					setDragging={setDragging}
					subLocations={subLocations || []}
					refetch={refetch}
					setSelectedAction={setSelectedAction}
					key={cargoAction?.id}
				/>
			))}
		</React.Fragment>
	);
};

const BerthingRow = ({ berthing, visit, dragging, setDragging, subLocations, refetch, setSelectedAction }: {
	berthing: ConnectingShipBerthing,
	visit: Visit,
	dragging: Dragging,
	setDragging: SetDragging,
	berthLocations?: Location[],
	subLocations?: SubLocations,
	refetch: () => void,
	setSelectedAction: (action: CargoAction) => void
}) => {
	const theme = useTheme();

	const confirm = useConfirm();

	const [ deleteBerth ] = useMutation(DeleteBerthingDocument);

	const cargoActions = [ ...(berthing.cargoActions || []) ];
	const rows = cargoActions.length;

	if (cargoActions.length === 0) {
		return null;
	}

	return [ ...Array(rows) ].map((_, index) => {

		const cargoAction = cargoActions[index] || null;
		const firstLoadingAction = cargoAction?.cargo?.firstLoadingAction;
		const lastDischargingAction = cargoAction?.cargo?.lastDischargingAction;

		const berthingWindowFrom = berthing.window?.from || 0;
		const berthingWindowTo = berthing.window?.to || 0;

		const berthingWindowClash = (berthingWindowFrom < (visit.from || 0)) || (berthingWindowTo > (visit.until || 0));


		return (
			<tr className="berthing-row" key={index}>
				{index === 0 ? (
					<React.Fragment>
						<td colSpan={2} style={{ backgroundColor: theme.palette.mode === "dark" ? theme.palette.background.paper : "#EEE" }} />
						<td colSpan={2}>
							<div className="actions">
								<IconButton
									size="small"
									onClick={() => {
										confirm({
											type: "WARNING",
											title: intl.formatMessage({ id: "CONNECTING_VISIT_CONFIRM_DELETE_BERTHING" }),
										}).then(() => {
											deleteBerth({
												variables: {
													id: Number(berthing.id),
												},
											}).then(() => {
												refetch();
											});
										});
									}}>
									<DeleteIcon fontSize="small" />
								</IconButton>
							</div>
						</td>

						<td>
							{dragging.enabled && visit.id ? (
								<DroppableLocation
									location={berthing.location}
									visitId={visit.id}
									berthingId={berthing.id}
									dragging={dragging}
									refetch={refetch} />
							) : (
								<EditableBerthingLocation
									berthing={berthing}
									locations={subLocations || []}
									refetch={refetch} />
							)}
						</td>

						<td className="dividerRight">
							<ClashField hasClash={berthingWindowClash}>
								<EditableBerthingWindow
									berthing={berthing}
									refetch={refetch} />
							</ClashField>
						</td>
					</React.Fragment>
				) : (
					<td colSpan={6} className="dividerRight" />
				)}

				{cargoAction ? (
					<React.Fragment>
						<td>
							<ClashField hasClash={!cargoAction.call?.voyage.ship} disablePadding>
								<DraggableShip cargoAction={cargoAction} setDragging={setDragging}>
									<strong>{cargoAction.call?.voyage.ship.name}</strong>
								</DraggableShip>
							</ClashField>
						</td>


						<td>
							{cargoAction.call ? (
								<EditableCallPta call={cargoAction.call} refetch={refetch} />
							) : (
								<CargoActionDate action={cargoAction} />
							)}
						</td>

						<td><CargoActionTypeTag type={cargoAction.type} size={TagSize.SMALL} /></td>

						<td><ProductLink product={cargoAction.cargo.product} /></td>
						<td>
							<CargoActionQuantity
								cargoAction={cargoAction}
								cargo={cargoAction.cargo}
								convertActualsToMton />
						</td>
						<td>{firstLoadingAction && (
							<CargoActionDate action={firstLoadingAction} />
						)}
						</td>
						<td>
							{firstLoadingAction && (
								<>
									{firstLoadingAction.call ? (
										<EditableCallLocation
											call={firstLoadingAction.call}
											locations={subLocations || []}
											refetch={refetch} />
									) : (
										firstLoadingAction.location?.description
									)}
								</>
							)}
						</td>
						<td>
							{lastDischargingAction && (
								<CargoActionDate action={lastDischargingAction} />
							)}
						</td>
						<td>
							{lastDischargingAction && (
								<>
									{lastDischargingAction.call ? (
										<EditableCallLocation
											call={lastDischargingAction.call}
											locations={subLocations || []}
											refetch={refetch} />
									) : (
										lastDischargingAction.location?.description
									)}
								</>
							)}
						</td>
						<td>
							<RelationLink relation={cargoAction.cargo?.charter?.debtor} />
						</td>
						<td>
							{cargoAction.cargo.charter && (
								<DossierLink charter={cargoAction.cargo.charter} />
							)}
						</td>
						<td>
							<span className="notes" onClick={() => setSelectedAction(cargoAction)}>
								<Tooltip title={cargoAction.connectingVisitNotes} placement="left" slotProps={{ tooltip: { sx: { whiteSpace: "pre-wrap" } } }}>
									<span style={{ display: "block", maxWidth: 150, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{cargoAction.connectingVisitNotes}</span>
								</Tooltip>
								{!cargoAction.connectingVisitNotes && (
									<div className="note-placeholder" />
								)}
							</span>
						</td>
					</React.Fragment>
				) : (
					<td colSpan={13} />
				)}
			</tr>
		);
	});
};

const CargoActionNotPlannedRow = ({ action, setDragging, subLocations, refetch, setSelectedAction }: {
	action: CargoAction,
	setDragging: SetDragging,
	subLocations: SubLocations,
	refetch: () => void,
	setSelectedAction: (action: CargoAction) => void
}) => {
	const theme = useTheme();
	const firstLoadingAction = action.cargo?.firstLoadingAction;
	const lastDischargingAction = action.cargo?.lastDischargingAction;

	return (
		<tr className="not-planned-row">
			<td colSpan={2} style={{ backgroundColor: theme.palette.mode === "dark" ? theme.palette.background.paper : "#EEE" }} />
			<td colSpan={2} />

			<td>
				<ClashField hasClash />
			</td>

			<td className="dividerRight">
				<ClashField hasClash />
			</td>

			<td>
				<ClashField hasClash={!action.call?.voyage} disablePadding>
					<DraggableShip cargoAction={action} setDragging={setDragging}>
						{action.call?.voyage.ship && <strong>{action.call.voyage.ship.name}</strong>}
					</DraggableShip>
				</ClashField>
			</td>


			<td>
				<ClashField hasClash={!action.call}>
					{action.call ? (
						<EditableCallPta call={action.call} refetch={refetch} />
					) : (
						<CargoActionDate action={action} format={format} />
					)}
				</ClashField>
			</td>

			<td><CargoActionTypeTag type={action.type} size={TagSize.SMALL} /></td>

			<td><ProductLink product={action.cargo.product} /></td>
			<td>{DataFormat.tonnage(action.weight)}</td>
			<td>
				{firstLoadingAction && (
					<CargoActionDate action={firstLoadingAction} />
				)}
			</td>

			<td>
				{firstLoadingAction && (
					<>
						{firstLoadingAction.call ? (
							<EditableCallLocation
								call={firstLoadingAction.call}
								locations={subLocations}
								refetch={refetch} />
						) : (
							firstLoadingAction.location?.description
						)}
					</>
				)}
			</td>

			<td>
				{lastDischargingAction && (
					<CargoActionDate action={lastDischargingAction} />
				)}
			</td>

			<td>
				{lastDischargingAction && (
					<>
						{lastDischargingAction.call ? (
							<EditableCallLocation
								call={lastDischargingAction.call}
								locations={subLocations}
								refetch={refetch} />
						) : (
							lastDischargingAction.location?.description
						)}
					</>
				)}
			</td>

			<td>
				<RelationLink relation={action.cargo?.charter?.debtor} />
			</td>

			<td><DossierLink charter={action.cargo.charter} /></td>
			<td style={{ width: 150 }}>
				<span className="notes"
					onClick={() => setSelectedAction(action)}>
					<Tooltip title={action.connectingVisitNotes || ""} placement="left" slotProps={{ tooltip: { sx: { whiteSpace: "pre-wrap" } } }}>
						<span style={{ display: "block", maxWidth: 150, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
							{action.connectingVisitNotes}
						</span>
					</Tooltip>
					{!action.connectingVisitNotes && (
						<div className="note-placeholder" />
					)}
				</span>
			</td>
		</tr>
	);
};

const EditableField = ({ onClick, children }: {
	onClick: () => void,
	children: ReactNode,
}) => {
	return (
		<span className="editable-field" onClick={onClick}>
			{children}
			<EditIcon />
		</span>
	);
};

const EditableVisitWindow = ({ visit, refetch }: {
	visit: Visit,
	refetch: () => void,
}) => {

	const [ updateVisitWindowDialogOpen, openUpdateVisitWindowDialog ] = useState(false);

	return (
		<React.Fragment>
			<EditableField onClick={() => openUpdateVisitWindowDialog(true)}>
				<FormattedDateWindow from={visit.from} to={visit.until} format={format} />
			</EditableField>

			{visit.id && (
				<UpdateVisitWindowDialog
					open={updateVisitWindowDialogOpen}
					onClose={() => openUpdateVisitWindowDialog(false)}
					visit={{ id: visit.id, from: visit.from ?? null, to: visit.until ?? null }}
					refetch={refetch} />
			)}
		</React.Fragment>
	);
};

const EditableBerthingLocation = ({ berthing, locations, refetch }: {
	berthing: ConnectingShipBerthing,
	locations: SubLocations,
	refetch: () => void,
}) => {

	const [ updateBerthingLocationDialogOpen, openUpdateBerthingLocationDialog ] = useState(false);

	return (
		<React.Fragment>
			<EditableField onClick={() => openUpdateBerthingLocationDialog(true)}>
				{berthing.location?.description}
			</EditableField>

			{berthing.id && (
				<UpdateBerthingLocationDialog
					open={updateBerthingLocationDialogOpen}
					onClose={() => openUpdateBerthingLocationDialog(false)}
					berthing={{
						id: berthing.id,
						location: berthing.location ?? null,
					}}
					locations={locations}
					refetch={refetch} />
			)}
		</React.Fragment>
	);
};

const EditableBerthingWindow = ({ berthing, refetch }: {
	berthing: ConnectingShipBerthing,
	refetch: () => void,
}) => {

	const [ updateBerthingWindowDialogOpen, openUpdateBerthingWindowDialog ] = useState(false);

	return (
		<React.Fragment>
			<EditableField onClick={() => openUpdateBerthingWindowDialog(true)}>
				<FormattedDateWindow from={berthing.window?.from} to={berthing.window?.to} format={format} />
			</EditableField>

			{berthing.id && (
				<UpdateBerthingWindowDialog
					open={updateBerthingWindowDialogOpen}
					onClose={() => openUpdateBerthingWindowDialog(false)}
					berthing={{
						id: berthing.id,
						from: berthing.window?.from ?? null,
						to: berthing.window?.to ?? null,
					}}
					refetch={refetch} />
			)}
		</React.Fragment>
	);
};

const EditableCallLocation = ({ call, locations, refetch }: {
	call: Pick<Call, "id"> & { location: LocationSelectOption },
	locations: SubLocations,
	refetch: () => void,
}) => {

	const [ updateCallLocationDialogOpen, openUpdateCallLocationDialog ] = useState(false);

	return (
		<React.Fragment>
			<EditableField onClick={() => openUpdateCallLocationDialog(true)}>
				{call.location?.description}
			</EditableField>

			<UpdateCallLocationDialog
				open={updateCallLocationDialogOpen}
				onClose={() => openUpdateCallLocationDialog(false)}
				call={{
					id: call.id,
					location: call.location ?? null,
				}}
				locations={locations}
				refetch={refetch} />
		</React.Fragment>
	);
};

const EditableCallPta = ({ call, refetch }: {
	call: Pick<Call, "id" | "pta">,
	refetch: () => void,
}) => {

	const [ updateCallPtaDialogOpen, openUpdateCallPtaDialog ] = useState(false);

	return (
		<React.Fragment>
			<EditableField onClick={() => openUpdateCallPtaDialog(true)}>
				<FormattedDate date={call.pta} format={format} />
			</EditableField>

			<UpdateCallPtaDialog
				open={updateCallPtaDialogOpen}
				onClose={() => openUpdateCallPtaDialog(false)}
				call={{ id: call.id, pta: call.pta ?? null }}
				refetch={refetch} />
		</React.Fragment>
	);
};

const DroppableLocation = ({ location, visitId, berthingId, refetch, dragging }: {
	location?: Location | null,
	visitId: number;
	berthingId?: number | null,
	refetch: () => void,
	dragging: Dragging
}) => {

	const confirm = useConfirm();

	const [ updateActionLocation ] = useMutation(updateCargoActionLocation_mutation);
	const [ linkActionToBerthing ] = useMutation(LinkActionToBerthingDocument);

	const hasMatchingLocation = location?.id === dragging.managedShipLocation?.id && (berthingId !== dragging.connectingShipBerthing?.id);

	return (
		<div
			className={"droppable-location"}
			onDragOver={(event) => event.preventDefault()}
			onDragEnter={(event) => {
				event.preventDefault();
				(event.target as HTMLElement).style.background = "rgba(41, 41, 41, 0.1)";
			}}
			onDragLeave={(event) => {
				event.preventDefault();
				(event.target as HTMLElement).style.background = "inherit";
			}}
			onDrop={(event) => {
				event.preventDefault();
				(event.target as HTMLElement).style.background = "inherit";
				const dataTransfer = JSON.parse(event.dataTransfer.getData("linkAction"));

				if (!hasMatchingLocation) {
					confirm({
						type: "WARNING",
						title: intl.formatMessage({ id: "CONNECTING_VISIT_CONFIRM_UPDATE_ACTION_LOCATION" }),
					}).then(() => {
						if (!location) {
							return;
						}
						updateActionLocation({
							variables: {
								actionId: dataTransfer.actionId,
								locationId: location.id,
							},
						}).then(() => {
							linkActionToBerthing({
								variables: {
									actionId: dataTransfer.actionId,
									visitId: visitId,
									berthingId: berthingId,
								},
							}).then(() => refetch());
						});
					});
				} else {
					confirm({
						type: "WARNING",
						title: intl.formatMessage({ id: "CONNECTING_VISIT_CONFIRM_PLAN_ACTION" }),
					}).then(() => {
						linkActionToBerthing({
							variables: {
								actionId: dataTransfer.actionId,
								visitId: visitId,
								berthingId: berthingId,
							},
						}).then(() => {
							refetch();
						});
					});
				}
			}}>
			{location?.description}
		</div>
	);
};

const DroppableVisitRow = ({ visit, dragging, refetch }: {
	visit?: Visit | null;
	dragging: Dragging;
	refetch: () => void;
}) => {
	const theme = useTheme();

	const confirm = useConfirm();

	const [ editConnectingVisitUpdateTime ] = useMutation(EditConnectingVisitUpdateTimeDocument);
	const [ linkActionToBerthing ] = useMutation(LinkActionToBerthingDocument);

	const [ selectedAction, setSelectedAction ] = useState<CargoAction | undefined>(undefined);
	const [ shipBerthingDialogOpen, setShipBerthingDialogOpen ] = useState(false);

	const canCreateBerthing = dragging.enabled && dragging.connectingShip != null && visit?.ship != null && dragging.connectingShip.id === visit.ship.id;

	const openCreateBerthingDialog = (action: CargoAction) => {
		setSelectedAction(action);
		setShipBerthingDialogOpen(true);
	};

	const closeBerthingDialog = () => {
		if (selectedAction) {
			setSelectedAction(undefined);
		}
		setShipBerthingDialogOpen(false);
	};

	return (
		<tr className={`${canCreateBerthing ? "droppable-visit-row-drag-start" : "droppable-visit-row"}`}
			onDragOver={(event) => event.preventDefault()}
			onDragEnter={(event) => {
				if (!canCreateBerthing) {
					return;
				}
				event.preventDefault();
				(event.currentTarget as HTMLTableRowElement).classList.add("droppable-visit-row-drag-enter");
			}}
			onDragLeave={(event) => {
				if (!canCreateBerthing) {
					return;
				}
				event.preventDefault();
				(event.currentTarget as HTMLTableRowElement).classList.remove("droppable-visit-row-drag-enter");
			}}
			onDrop={(event) => {
				if (!canCreateBerthing) {
					return;
				}
				event.preventDefault();
				const dataTransfer = JSON.parse(event.dataTransfer.getData("linkAction"));
				const action = visit?.cargoActions?.find(action => action?.id === dataTransfer.actionId);
				if (!action) {
					return;
				}

				if (visit && dragging.connectingShipBerthing) {
					confirm({
						type: "WARNING",
						title: intl.formatMessage({ id: "CONNECTING_VISIT_CONFIRM_UNPLAN_CARGO_ACTION" }),
					}).then(() => {
						linkActionToBerthing({
							variables: {
								actionId: dataTransfer.actionId,
								visitId: visit.id,
								berthingId: null,
							},
						}).then(() => {
							refetch();
						});
					});
				} else {
					openCreateBerthingDialog(action);
				}
			}}
		>
			<td colSpan={2} style={{ backgroundColor: theme.palette.mode === "dark" ? theme.palette.background.paper : "#EEE" }}>
				<span className="last-update">
					<Button
						icon={<RefreshIcon fontSize="small" />}
						onClick={() => {
							confirm({
								type: "WARNING",
								title: intl.formatMessage({ id: "CONNECTING_VISIT_UPDATE_LAST_UPDATE_TIME_CONFIRM" }),
							}).then(() => {
								if (visit?.id) {
									editConnectingVisitUpdateTime({
										variables: { id: visit.id },
									}).then(() => {
										refetch();
									});
								}
							});
						}}
						variant="text"
						size="small">
						<FormattedDate date={visit?.lastUpdateFromAgentOn} format="DD/MM HH:mm" />
					</Button>
				</span>
			</td>

			<td>
				<strong>{visit?.ship?.name}</strong>
			</td>

			<td colSpan={3} className="dividerRight">
				{visit && (
					<EditableVisitWindow visit={visit} refetch={refetch} />
				)}
			</td>

			<td colSpan={13}>
				<Button
					disabled={dragging.enabled && dragging.connectingShipBerthing != null}
					icon={<AddIcon fontSize="small" />}
					onClick={() => setShipBerthingDialogOpen(true)}
					style={{ padding: "0 4px", height: 20, fontSize: 11 }}
					variant="text"
					size="small">
					{intl.formatMessage({ id: "ADD_BERTH" })}
				</Button>

				{shipBerthingDialogOpen && visit?.id && (
					<CreateConnectingShipBerthingDialog
						open={shipBerthingDialogOpen}
						onClose={closeBerthingDialog}
						visitId={visit.id}
						action={{
							id: selectedAction?.id ?? null,
							location: selectedAction?.location ?? null,
							start: selectedAction?.start ?? null,
							end: selectedAction?.end ?? null,
						}}
						refetch={refetch} />
				)}
			</td>
		</tr>
	);
};

const DraggableShip = ({ cargoAction, setDragging, children }: {
	cargoAction: CargoAction,
	setDragging: SetDragging,
	children: ReactNode,
}) => {
	return (
		<span
			className="draggable"
			onDragStart={event => {
				event.dataTransfer.setData("linkAction", JSON.stringify({ actionId: cargoAction.id }));
				setDragging({
					enabled: true,
					connectingShip: cargoAction.connectingShipPortVisit?.ship || null,
					managedShipLocation: cargoAction.location || null,
					connectingShipBerthing: cargoAction.connectingShipBerthing || null,
				});
			}}
			onDragEnd={() => {
				setDragging({
					enabled: false,
					connectingShip: null,
					managedShipLocation: null,
					connectingShipBerthing: null,
				});
			}}
			draggable>
			{children}
		</span>
	);
};

export default ConnectingVisitPlanning;
